/**
 * Index of the body under the cursor, -1 if none. A cursor inside a body's actual disc wins over
 * inflated hit areas, nearest to the camera first; otherwise the inflated hit closest to the cursor wins.
 */
export function pickNearest(
  screen: Float32Array,
  radiiPx: Float32Array,
  count: number,
  x: number,
  y: number,
  minRadiusPx: number,
  visible?: Uint8Array,
): number {
  let disc = -1,
    discW = Infinity;
  let near = -1,
    nearD2 = Infinity;
  for (let k = 0; k < count; k++) {
    const o = 3 * k;
    const w = screen[o + 2];
    if (w <= 0 || visible?.[k] === 0) continue;
    const dx = screen[o] - x,
      dy = screen[o + 1] - y;
    const d2 = dx * dx + dy * dy;
    const r = radiiPx[k];
    if (d2 <= r * r) {
      if (w < discW) {
        disc = k;
        discW = w;
      }
      continue;
    }
    const ri = Math.max(r, minRadiusPx);
    if (d2 <= ri * ri && d2 < nearD2) {
      near = k;
      nearD2 = d2;
    }
  }
  return disc >= 0 ? disc : near;
}

/**
 * Squared pixel distance from (x, y) to the nearest of `segments` projected segments, stored as
 * consecutive point pairs of (x, y, clip w). Segments with an end behind the camera are skipped.
 */
export function nearestSegmentD2(
  screen: Float32Array,
  segments: number,
  x: number,
  y: number,
): number {
  let best = Infinity;
  for (let s = 0; s < segments; s++) {
    const o = 6 * s;
    if (screen[o + 2] <= 0 || screen[o + 5] <= 0) continue;
    const ax = screen[o],
      ay = screen[o + 1];
    const dx = screen[o + 3] - ax,
      dy = screen[o + 4] - ay;
    const len2 = dx * dx + dy * dy;
    const t = len2 === 0 ? 0 : Math.min(Math.max(((x - ax) * dx + (y - ay) * dy) / len2, 0), 1);
    const ex = ax + t * dx - x,
      ey = ay + t * dy - y;
    best = Math.min(best, ex * ex + ey * ey);
  }
  return best;
}
